import { Role } from '@/types/Role'
import { Daycare } from '@/types/Daycare'
import { FunctionComponent, JSX, useCallback, useEffect, useState } from 'react'
import styles from './eventComingSoon.module.scss'
import Title from '@/components/_custom/Title/title'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useTranslation } from 'react-i18next'
import { useLazyQuery, useQuery } from '@apollo/client'
import { EventDay } from '@/types/EventDay'
import { DAYCARE_HAVE_PASSED_EVENT, GET_EVENT_DAY } from '@/modules/API/daycare/eventDayHandler'
import moment from 'moment/moment'
import { ClosingDay } from '@/types/ClosingDays'
import { GET_CLOSING_DAY } from '@/modules/API/daycare/closingDayHandler'
import Loading from '@/components/Loading/Loading'
import { ArrowBack, ArrowForward } from '@mui/icons-material'
import AddEventsClosingDaysDialog from '../../events/Dialog/addEventsClosingDays'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { GET_TASK_NOTES_BY_DAYCARE } from '@/modules/API/taskNote/getTaskNotes.ts'
import { TaskNote, TaskNoteType } from '@/types/TaskNote.ts'
import EventDayRow from '../../events/eventDay'
import ClosingDayRow from '../../events/closingDay'
import TaskNoteComponent from '../../taskNote/taskNoteComponent/taskNoteComponent.tsx'
import { useRefetchOnFocusOrOnline } from '@/hooks/useRefetchOnFocusOrOnline.ts'

interface NewsFeedProps {
    role: Role
    daycare?: Daycare
    canUpdate: boolean
}

const EventComingSoon: FunctionComponent<NewsFeedProps> = ({ role, daycare, canUpdate }: NewsFeedProps) => {
    const { t } = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const [eventDayList, setEventDayList] = useState<Array<EventDay>>([])

    const {
        loading: loadingEventDay,
        data: dataEventDay,
        refetch: refetchEventDays
    } = useQuery<{
        getEventDay: EventDay[]
    }>(GET_EVENT_DAY, {
        fetchPolicy: 'network-only',
        variables: {
            daycare_id: daycare?.id,
            from: moment().format('DD-MM-YYYY')
        }
    })

    useEffect(() => {
        if (dataEventDay) {
            setEventDayList(
                dataEventDay.getEventDay
                    .slice()
                    .sort((a: EventDay, b: EventDay) =>
                        moment(a.date, 'DD-MM-YYYY').toDate() >= moment(b.date, 'DD-MM-YYYY').toDate() ? 1 : -1
                    )
            )
        }
    }, [dataEventDay])

    const [closingDaysList, setClosingDaysList] = useState<Array<ClosingDay>>([])

    const {
        loading: loadingClosingDay,
        data: dataClosingDay,
        refetch: refetchClosingDays
    } = useQuery<{
        getClosingDay: ClosingDay[]
    }>(GET_CLOSING_DAY, {
        fetchPolicy: 'network-only',
        variables: {
            daycare_id: daycare?.id,
            from: moment().format('DD-MM-YYYY')
        }
    })

    const [havePassedEvents, setHavePassedEvents] = useState<boolean>(false)

    const {
        loading: loadingDaycareHavePassedEvents,
        data: dataDaycareHavePassedEvents,
        refetch: refetchDaycareHavePassedEvents
    } = useQuery<{
        daycareHavePassedEvents: boolean
    }>(DAYCARE_HAVE_PASSED_EVENT, {
        fetchPolicy: 'network-only',
        variables: {
            daycareId: daycare?.id,
            date: moment().format('DD-MM-YYYY')
        }
    })

    useEffect(() => {
        if (dataDaycareHavePassedEvents) {
            setHavePassedEvents(role !== Role.parent && dataDaycareHavePassedEvents.daycareHavePassedEvents)
        }
    }, [dataDaycareHavePassedEvents])

    useEffect(() => {
        if (dataClosingDay) {
            setClosingDaysList(
                dataClosingDay.getClosingDay
                    .slice()
                    .sort((a: ClosingDay, b: ClosingDay) =>
                        moment(a.startDate, 'DD-MM-YYYY').toDate() > moment(b.startDate, 'DD-MM-YYYY').toDate() ? 1 : -1
                    )
            )
        }
    }, [dataClosingDay])

    const [taskNoteList, setTaskNoteList] = useState<Array<TaskNote>>([])

    const [getTaskNotes, { loading: loadingTaskNote, data: dataTaskNote, refetch: refetchTaskNote }] = useLazyQuery(
        GET_TASK_NOTES_BY_DAYCARE,
        {
            fetchPolicy: 'network-only',
            variables: {
                daycareId: daycare?.id,
                from: moment().format('DD-MM-YYYY')
            }
        }
    )

    useEffect(() => {
        if (
            role === Role.parent &&
            dataTaskNote &&
            dataTaskNote.getTaskNotesByDaycare &&
            dataTaskNote.getTaskNotesByDaycare.length > 0
        ) {
            setTaskNoteList(
                dataTaskNote.getTaskNotesByDaycare
                    .filter(
                        (taskNote: TaskNote): boolean =>
                            taskNote.type === TaskNoteType.task ||
                            taskNote.type === TaskNoteType.appointmentInPerson ||
                            taskNote.type === TaskNoteType.appointmentVideoconference
                    )
                    .filter((taskNote: TaskNote): boolean => taskNote.date !== undefined && taskNote.startHour !== null)
                    .slice()
                    .sort((a: TaskNote, b: TaskNote): number =>
                        a.date === b.date
                            ? moment(a.startHour, 'HH:mm').format('HHmm') < moment(b.startHour, 'HH:mm').format('HHmm')
                                ? 1
                                : -1
                            : moment(a.date, 'DD-MM-YYYY').toDate() >= moment(b.date, 'DD-MM-YYYY').toDate()
                              ? 1
                              : -1
                    )
            )
        }
    }, [dataTaskNote])

    useEffect(() => {
        if (role === Role.parent) {
            getTaskNotes()
        }
    }, [daycare])

    type ItemForSorting =
        | { type: 'event'; value: EventDay }
        | { type: 'closing'; value: ClosingDay }
        | { type: 'taskNote'; value: TaskNote }

    function getFirstEvent(
        eventDayList: EventDay[],
        closingDaysList: ClosingDay[],
        taskNoteList: TaskNote[]
    ): JSX.Element {
        const items: ItemForSorting[] = [
            ...eventDayList.map((e: EventDay): ItemForSorting => ({ type: 'event', value: e })),
            ...closingDaysList.map(
                (c: ClosingDay): ItemForSorting => ({
                    type: 'closing',
                    value: c
                })
            ),
            ...taskNoteList.map((t: TaskNote): ItemForSorting => ({ type: 'taskNote', value: t }))
        ]

        if (items.length === 0) return <p className={styles.NoEventsClosingDaysText}>{t('noEventsClosingDays')}</p>

        const firstItem: ItemForSorting = items.sort((a: ItemForSorting, b: ItemForSorting): number => {
            const da: Date = moment(
                a.type === 'event' ? a.value.date : a.type === 'closing' ? a.value.startDate : a.value.date,
                'DD-MM-YYYY'
            ).toDate()

            const db: Date = moment(
                b.type === 'event' ? b.value.date : b.type === 'closing' ? b.value.startDate : b.value.date,
                'DD-MM-YYYY'
            ).toDate()

            return da.getTime() - db.getTime()
        })[0]

        switch (firstItem.type) {
            case 'event':
                return (
                    <EventDayRow
                        event={firstItem.value}
                        isParent={role === Role.parent}
                        canUpdate={canUpdate}
                        refetchAllEvents={refetchAllEvents}
                        daycare={daycare}
                        key={`${firstItem.value.id}${firstItem.value.booked ? '_booked' : '_notBooked'}`}
                    />
                )
            case 'closing':
                return (
                    <ClosingDayRow
                        closingDay={firstItem.value}
                        parent={role === Role.parent}
                        canUpdate={canUpdate}
                        daycare={daycare}
                        closingDays={closingDaysList}
                        refetchAllEvents={refetchAllEvents}
                    />
                )
            case 'taskNote':
                return (
                    <TaskNoteComponent
                        taskNote={firstItem.value}
                        isParent={role === Role.parent}
                        isRpeOrLaep={daycare?.type === 'rpe' || daycare?.type === 'laep'}
                        canUpdate={canUpdate}
                        daycareId={daycare!.id}
                        handleRefetch={refetchAllEvents}
                    />
                )
            default:
                return <></>
        }
    }

    const [isAddEventsClosingDaysDialog, setAddEventsClosingDaysDialog] = useState<boolean>(false)

    const refetchAllEvents: () => Promise<void> = useCallback(async () => {
        const { data } = await refetchEventDays()

        if (data.getEventDay && data.getEventDay.length > 0) {
            setEventDayList(
                data.getEventDay
                    .slice()
                    .sort((a: EventDay, b: EventDay) =>
                        moment(a.date, 'DD-MM-YYYY').toDate() >= moment(b.date, 'DD-MM-YYYY').toDate() ? 1 : -1
                    )
            )
        } else {
            setEventDayList([])
        }

        const { data: dataClosingDay } = await refetchClosingDays()

        if (dataClosingDay.getClosingDay && dataClosingDay.getClosingDay.length > 0) {
            setClosingDaysList(
                dataClosingDay.getClosingDay
                    .slice()
                    .sort((a: ClosingDay, b: ClosingDay) =>
                        moment(a.startDate, 'DD-MM-YYYY').toDate() > moment(b.startDate, 'DD-MM-YYYY').toDate() ? 1 : -1
                    )
            )
        } else {
            setClosingDaysList([])
        }

        const { data: dataDaycareHavePassedEvents } = await refetchDaycareHavePassedEvents()

        if (dataDaycareHavePassedEvents) {
            setHavePassedEvents(role !== Role.parent && dataDaycareHavePassedEvents.daycareHavePassedEvents)
        }

        if (role === Role.parent) {
            const { data: dataTaskNote } = await refetchTaskNote()

            if (dataTaskNote && dataTaskNote.getTaskNotesByDaycare && dataTaskNote.getTaskNotesByDaycare.length > 0) {
                setTaskNoteList(
                    dataTaskNote.getTaskNotesByDaycare
                        .filter(
                            (taskNote: TaskNote): boolean =>
                                taskNote.type === TaskNoteType.task ||
                                taskNote.type === TaskNoteType.appointmentInPerson ||
                                taskNote.type === TaskNoteType.appointmentVideoconference
                        )
                        .filter(
                            (taskNote: TaskNote): boolean => taskNote.date !== undefined && taskNote.startHour !== null
                        )
                        .slice()
                        .sort((a: TaskNote, b: TaskNote): number =>
                            a.date === b.date
                                ? moment(a.startHour, 'HH:mm').format('HHmm') <
                                  moment(b.startHour, 'HH:mm').format('HHmm')
                                    ? 1
                                    : -1
                                : moment(a.date, 'DD-MM-YYYY').toDate() >= moment(b.date, 'DD-MM-YYYY').toDate()
                                  ? 1
                                  : -1
                        )
                )
            } else {
                setTaskNoteList([])
            }
        }
    }, [
        refetchEventDays,
        setEventDayList,
        refetchClosingDays,
        setClosingDaysList,
        refetchDaycareHavePassedEvents,
        setHavePassedEvents,
        role,
        refetchTaskNote,
        setTaskNoteList
    ])

    useRefetchOnFocusOrOnline(refetchAllEvents)

    const moreButtonText: string =
        `${eventDayList.length + closingDaysList.length + taskNoteList.length - 1}` +
        ' ' +
        `${eventDayList.length + closingDaysList.length + taskNoteList.length - 1 === 1 ? t('eventComingSoon') : t('eventsComingSoon')}`

    return (
        <div className={styles.EventComingSoonPlacement}>
            <div className={styles.Form}>
                <div className={`${styles.TitleAndButton} ${canUpdate && role !== Role.parent && styles.MarginBottom}`}>
                    <Title text={t('comingSoon')} className={styles.Title} />
                    {canUpdate && role !== Role.parent && (
                        <ButtonTextIcon
                            type={'add'}
                            className={styles.Button}
                            onClick={() => setAddEventsClosingDaysDialog(true)}
                        />
                    )}
                </div>
                {(loadingEventDay || loadingClosingDay || loadingDaycareHavePassedEvents || loadingTaskNote) && (
                    <Loading overall={false} />
                )}
                <div className={styles.Container}>
                    {eventDayList.length > 0 || closingDaysList.length > 0 || taskNoteList.length > 0 ? (
                        getFirstEvent(eventDayList, closingDaysList, taskNoteList)
                    ) : (
                        <p className={styles.NoEventsClosingDaysText}>{t('noEventsClosingDays')}</p>
                    )}
                    <div className={styles.ButtonContainer}>
                        {havePassedEvents && (
                            <ButtonTextIcon
                                type={'custom'}
                                className={
                                    eventDayList.length + closingDaysList.length > 1
                                        ? styles.ShowDoubleMoreButton
                                        : styles.ShowMoreButton
                                }
                                customText={t('showPassedEvents')}
                                customIcon={<ArrowBack />}
                                onClick={() =>
                                    navigate(
                                        `/events?fromDate=${moment()
                                            .subtract(1, 'years')
                                            .format('DD-MM-YYYY')}&toDate=${moment()
                                            .subtract(1, 'days')
                                            .format('DD-MM-YYYY')}`
                                    )
                                }
                                parent={role === Role.parent}
                            />
                        )}
                        {eventDayList.length + closingDaysList.length + taskNoteList.length > 1 && (
                            <ButtonTextIcon
                                type={'custom'}
                                className={havePassedEvents ? styles.ShowDoubleMoreButton : styles.ShowMoreButton}
                                customText={moreButtonText}
                                customIcon={<ArrowForward />}
                                onClick={() =>
                                    navigate('/events?toDate=' + moment().add(1, 'years').format('DD-MM-YYYY'))
                                }
                                parent={role === Role.parent}
                            />
                        )}
                    </div>
                </div>
            </div>
            <AddEventsClosingDaysDialog
                open={isAddEventsClosingDaysDialog}
                daycareId={daycare?.id || 0}
                daycareType={daycare?.type || undefined}
                closingDays={closingDaysList}
                refetchAllEvents={refetchAllEvents}
                handleClose={() => setAddEventsClosingDaysDialog(false)}
            />
        </div>
    )
}

export default EventComingSoon
